type CacheItem = Excite.ContentApi.IComponentData;

const loadedComponents: { [name: string]: React.ComponentType<any> } = {};

export type ComponentRegistry = {
  [name: string]: () => Promise<{ component: React.ComponentType<any> }>;
};
let componentRegistry: ComponentRegistry;
let resolveComponentRegistrySet: (value?: unknown) => unknown;
const componentRegistrySet = new Promise(resolve => {
  resolveComponentRegistrySet = resolve;
});

export function setComponentRegistry(registry: ComponentRegistry) {
  componentRegistry = registry;
  resolveComponentRegistrySet();
}

function componentHasLoaded(componentName: string, component: React.ComponentType<any>) {
  loadedComponents[componentName] = component;
}

export function hasComponentLoaded(componentName: string) {
  return !!loadedComponents[componentName];
}

export async function resolveComponent(componentName: string) {
  await componentRegistrySet;
  const componentLoader = componentRegistry[componentName];
  if (!componentLoader) {
    if (__DEV__) {
      throw new Error(
        "Component '" +
          componentName +
          "' does not exist. " +
          'Did you forget to build the frontend or run scope.cmd frontend:build-component-registry?' +
          'It could also be that the component file is missing a correct @' +
          'ComponentFor notation',
      );
    } else {
      return Promise.reject(new Error("Component '" + componentName + "' does not exist."));
    }
  }
  return componentLoader().then(
    component => {
      if (!component.component) {
        if (__DEV__) {
          throw new Error(
            "Component '" +
              componentName +
              "' could not be loaded. " +
              'Did you forget to default export your component in your file?',
          );
        }
      }
      componentHasLoaded(componentName, component.component);
      return component;
    },
    e => {
      console.error(e);
      const err: BundleLoadError = new Error('Failed to load component: ' + componentName + ': ' + e) as any;
      err.additionalChunkLoadError = e || true;
      return Promise.reject(err);
    },
  );
}

export function allComponentsAreResolved<TData>(data: TData) {
  const componentNames = findAllComponentNames(data);
  const notYetLoadedComponents = componentNames.filter(c => !hasComponentLoaded(c));
  return notYetLoadedComponents.length === 0;
}

export function loadAllComponents() {
  const promises: Promise<unknown>[] = [];
  for (const componentName in componentRegistry) {
    promises.push(resolveComponent(componentName));
  }
  return Promise.all(promises);
}

/**
 * Returns a promise if there are components to resolve, returns undefined of no components
 * are found in the data or if they have all been resolved already
 */
export function resolveComponentAndChildComponents<TData>(data: TData) {
  const componentNames = findAllComponentNames(data);
  if (!componentNames.length) {
    return undefined;
  }

  const existingComponents = componentNames.filter(n => n in componentRegistry);

  if (existingComponents.every(c => hasComponentLoaded(c))) {
    return undefined;
  }

  return Promise.all(existingComponents.map(componentName => resolveComponent(componentName))).then(
    () => {
      return data;
    },
    e => {
      console.error(e);
      const err: BundleLoadError = new Error('Failed to load additional chunks: ' + e) as any;
      err.additionalChunkLoadError = e || true;
      return Promise.reject(err);
    },
  );
}

export function findAllComponentNames(object: any, isRoot = true, componentNames: string[] = []) {
  if (!object) {
    return componentNames;
  }
  Object.keys(object).forEach(key => {
    if (key === 'componentName') {
      const cacheItem = object as CacheItem;
      // We only load partials if it's on the root object because that component is needed to render
      // the root. A deep partial has a component name so that you can navigate to it quickly, but it
      // isn't used to render the root.
      if (
        // Because backend might sometimes send out component names for components that hasn't been
        // registered by the frontend
        cacheItem.componentName in componentRegistry &&
        !componentNames.includes(cacheItem.componentName) &&
        (isRoot || cacheItem.cache !== 'partial')
      ) {
        componentNames.push(cacheItem.componentName);
      }
    } else if (typeof object[key] === 'object') {
      findAllComponentNames(object[key], false, componentNames);
    } else if (Array.isArray(object[key])) {
      (object[key] as Object[]).forEach(o => {
        findAllComponentNames(o, false, componentNames);
      });
    }
  });
  return componentNames;
}

export function resolveSyncIfLoadedOrThrow(componentName: string): React.ComponentType<any> {
  const component = loadedComponents[componentName];
  if (!component) {
    if (__DEV__) {
      // In development mode we return an empty component to prevent it to
      // fail. This is for cases when content exists in CMS but not the code to
      // handle it is missing.
      console.error(
        `The component ${componentName} has not been loaded. An empty component was rendered instead.`,
        'Loaded components:',
        loadedComponents,
      );
      return () => null;
    } else {
      console.error('Loaded components:', loadedComponents);
      throw new Error(
        `The component ${componentName} has not been loaded. It needs to be loaded before trying to render it.`,
      );
    }
  }
  return component;
}

export type BundleLoadError = Error & {
  additionalChunkLoadError: any;
};

export function isBundleLoadError(e: Object): e is BundleLoadError {
  const loadError = e as BundleLoadError;
  return !!loadError.additionalChunkLoadError;
}
